import { z } from 'zod';
import {
  CreateTenantUserRequestSchema,
  UpdateTenantUserRequestSchema,
  GetTenantUsersRequestSchema,
  CreateGroupRequestSchema,
  UpdateGroupRequestSchema,
  GetGroupsRequestSchema,
  CreateCustomerRequestSchema,
  UpdateCustomerRequestSchema,
  GetCustomersRequestSchema,
  SendInvitationRequestSchema,
  ResetPasswordRequestSchema,
  CreateWaferRequestSchema,
  UpdateWaferRequestSchema,
  GetWafersRequestSchema,
  CreateContainerRequestSchema,
  UpdateContainerRequestSchema,
  GetContainersRequestSchema,
  WaferTransferRequestSchema,
  CreateBeaconConfigRequestSchema,
  UpdateBeaconConfigRequestSchema,
  GetBeaconConfigsRequestSchema,
  CreateLocationZoneRequestSchema,
  UpdateLocationZoneRequestSchema,
  GetLocationZonesRequestSchema,
  UserMembershipRequestSchema,
  UpdateTenantSchema,
  CreateTenantUserSchema,
  // Response Schemas
  WaferResponseSchema,
  ContainerResponseSchema,
  PaginatedResponseSchema
} from '@cnapse/shared/schemas.js';

export interface ValidationError {
  field: string;
  message: string;
  code?: string;
  received?: string;
  expected?: string;
  value?: unknown;
}

export interface ValidationResult<T> {
  success: boolean;
  data?: T;
  errors?: ValidationError[];
  debug?: {
    totalErrors: number;
    originalDataKeys: string | string[];
    timestamp: string;
    schemaName?: string;
    currentUrl?: string;
    currentPath?: string;
    userAgent?: string;
  };
}

/**
 * Helper function to get nested value from object path
 */
const getNestedValue = (obj: unknown, path: PropertyKey[]): unknown => {
  if (!obj || path.length === 0) return obj;

  try {
    return path.reduce<unknown>((current, key) => {
      return current && typeof current === 'object' ? (current as Record<PropertyKey, unknown>)[key] : undefined;
    }, obj);
  } catch {
    // Defensive: hostile objects may throw on property access during error formatting.
    return undefined;
  }
};

export function readNestedValueForValidation(obj: unknown, path: PropertyKey[]): unknown {
  return getNestedValue(obj, path);
}

/**
 * Enhanced error formatting for better debugging with URL context
 */
const formatZodError = (error: z.ZodError, originalData: unknown, schemaName?: string): ValidationError[] => {
  const formattedErrors = error.issues.map((err: z.ZodIssue) => {
    const field = err.path.length > 0 ? err.path.join('.') : 'root';
    const invalidTypeIssue = err.code === 'invalid_type' ? (err as z.ZodInvalidTypeIssue) : undefined;
    return {
      field,
      message: err.message,
      code: err.code,
      received: invalidTypeIssue?.received,
      expected: invalidTypeIssue?.expected,
      value: getNestedValue(originalData, err.path)
    };
  });

  // Console log detailed error for debugging (only in development)
  if (process.env.NODE_ENV === 'development') {
    const currentUrl = typeof window !== 'undefined' ? window.location.href : 'unknown';
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : 'unknown';

    console.group(`🔍 Frontend Validation Error${schemaName ? ` - ${schemaName}` : ''}`);
    console.error('🌐 Current URL:', currentUrl);
    console.error('📍 Current Path:', currentPath);
    console.error('📋 Original Data:', JSON.stringify(originalData, null, 2));
    console.error('📊 Error Count:', error.issues.length);
    console.table(formattedErrors.map((err: ValidationError) => ({
      Field: err.field,
      Message: err.message,
      Code: err.code,
      Expected: err.expected,
      Received: err.received,
      Value: typeof err.value === 'object' ? JSON.stringify(err.value) : err.value
    })));
    console.groupEnd();
  }

  return formattedErrors;
};

/**
 * Generic validation function using Zod schemas with enhanced debugging and URL context
 */
export function validateData<T>(
  schema: { parse: (data: unknown) => T },
  data: unknown,
  schemaName?: string
): ValidationResult<T> {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : 'unknown';
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : 'unknown';

  try {
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔍 Frontend Validation: Starting validation${schemaName ? ` for ${schemaName}` : ''}`, {
        dataKeys: data && typeof data === 'object' && data !== null ? Object.keys(data) : 'non-object',
        dataType: typeof data,
        schemaName,
        currentUrl,
        currentPath
      });
    }

    const validatedData = schema.parse(data);

    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ Frontend Validation: Success${schemaName ? ` for ${schemaName}` : ''}`);
    }

    return {
      success: true,
      data: validatedData,
      debug: {
        totalErrors: 0,
        originalDataKeys: data && typeof data === 'object' && data !== null ? Object.keys(data) : 'non-object',
        timestamp: new Date().toISOString(),
        schemaName,
        currentUrl,
        currentPath,
        userAgent: typeof window !== 'undefined' ? navigator.userAgent : 'unknown'
      }
    };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = formatZodError(error, data, schemaName);

      return {
        success: false,
        errors,
        debug: {
          totalErrors: error.issues.length,
          originalDataKeys: data && typeof data === 'object' && data !== null ? Object.keys(data) : 'non-object',
          timestamp: new Date().toISOString(),
          schemaName,
          currentUrl,
          currentPath,
          userAgent: typeof window !== 'undefined' ? navigator.userAgent : 'unknown'
        }
      };
    }

    // Handle unexpected errors
    console.error(`💥 Unexpected validation error${schemaName ? ` for ${schemaName}` : ''} at ${currentUrl}:`, error);
    return {
      success: false,
      errors: [{ field: 'unknown', message: 'Validation failed due to unexpected error' }],
      debug: {
        totalErrors: 1,
        originalDataKeys: data && typeof data === 'object' && data !== null ? Object.keys(data) : 'non-object',
        timestamp: new Date().toISOString(),
        schemaName,
        currentUrl,
        currentPath,
        userAgent: typeof window !== 'undefined' ? navigator.userAgent : 'unknown'
      }
    };
  }
}

/**
 * Specific validation functions for different request types with enhanced debugging
 */
export const validateCreateUserRequest = (data: unknown) =>
  validateData(CreateTenantUserRequestSchema, data, 'CreateTenantUserRequest');

export const validateUpdateUserRequest = (data: unknown) =>
  validateData(UpdateTenantUserRequestSchema, data, 'UpdateTenantUserRequest');

export const validateGetUsersRequest = (data: unknown) =>
  validateData(GetTenantUsersRequestSchema, data, 'GetTenantUsersRequest');

export const validateCreateGroupRequest = (data: unknown) =>
  validateData(CreateGroupRequestSchema, data, 'CreateGroupRequest');

export const validateUpdateGroupRequest = (data: unknown) =>
  validateData(UpdateGroupRequestSchema, data, 'UpdateGroupRequest');

export const validateGetGroupsRequest = (data: unknown) =>
  validateData(GetGroupsRequestSchema, data, 'GetGroupsRequest');

export const validateCreateCustomerRequest = (data: unknown) =>
  validateData(CreateCustomerRequestSchema, data, 'CreateCustomerRequest');

export const validateUpdateCustomerRequest = (data: unknown) =>
  validateData(UpdateCustomerRequestSchema, data, 'UpdateCustomerRequest');

export const validateGetCustomersRequest = (data: unknown) =>
  validateData(GetCustomersRequestSchema, data, 'GetCustomersRequest');

export const validateSendInvitationRequest = (data: unknown) =>
  validateData(SendInvitationRequestSchema, data, 'SendInvitationRequest');

export const validateResetPasswordRequest = (data: unknown) =>
  validateData(ResetPasswordRequestSchema, data, 'ResetPasswordRequest');

export const validateCreateWaferRequest = (data: unknown) =>
  validateData(CreateWaferRequestSchema, data, 'CreateWaferRequest');

export const validateUpdateWaferRequest = (data: unknown) =>
  validateData(UpdateWaferRequestSchema, data, 'UpdateWaferRequest');

export const validateGetWafersRequest = (data: unknown) =>
  validateData(GetWafersRequestSchema, data, 'GetWafersRequest');

export const validateCreateContainerRequest = (data: unknown) =>
  validateData(CreateContainerRequestSchema, data, 'CreateContainerRequest');

export const validateUpdateContainerRequest = (data: unknown) =>
  validateData(UpdateContainerRequestSchema, data, 'UpdateContainerRequest');

export const validateGetContainersRequest = (data: unknown) =>
  validateData(GetContainersRequestSchema, data, 'GetContainersRequest');

export const validateWaferTransferRequest = (data: unknown) =>
  validateData(WaferTransferRequestSchema, data, 'WaferTransferRequest');

export const validateCreateBeaconConfigRequest = (data: unknown) =>
  validateData(CreateBeaconConfigRequestSchema, data, 'CreateBeaconConfigRequest');

export const validateUpdateBeaconConfigRequest = (data: unknown) =>
  validateData(UpdateBeaconConfigRequestSchema, data, 'UpdateBeaconConfigRequest');

export const validateGetBeaconConfigsRequest = (data: unknown) =>
  validateData(GetBeaconConfigsRequestSchema, data, 'GetBeaconConfigsRequest');

export const validateCreateLocationZoneRequest = (data: unknown) =>
  validateData(CreateLocationZoneRequestSchema, data, 'CreateLocationZoneRequest');

export const validateUpdateLocationZoneRequest = (data: unknown) =>
  validateData(UpdateLocationZoneRequestSchema, data, 'UpdateLocationZoneRequest');

export const validateGetLocationZonesRequest = (data: unknown) =>
  validateData(GetLocationZonesRequestSchema, data, 'GetLocationZonesRequest');

export const validateUserMembershipRequest = (data: unknown) =>
  validateData(UserMembershipRequestSchema, data, 'UserMembershipRequest');

// Response validation functions with enhanced debugging
export const validateWaferResponse = (data: unknown) =>
  validateData(WaferResponseSchema, data, 'WaferResponse');

export const validateContainerResponse = (data: unknown) =>
  validateData(ContainerResponseSchema, data, 'ContainerResponse');

export const validatePaginatedResponse = (data: unknown) =>
  validateData(PaginatedResponseSchema, data, 'PaginatedResponse');

/**
 * Utility function to format validation errors for display with enhanced information
 */
export function formatValidationErrors(errors: ValidationError[]): string {
  if (!errors || errors.length === 0) return 'No validation errors';

  return errors.map((error, index) => {
    let errorString = `${index + 1}. ${error.field}: ${error.message}`;

    if (error.code) {
      errorString += ` (Code: ${error.code})`;
    }

    if (error.expected && error.received) {
      errorString += ` | Expected: ${error.expected}, Received: ${error.received}`;
    }

    if (error.value !== undefined) {
      const valueString = typeof error.value === 'object'
        ? JSON.stringify(error.value)
        : String(error.value);
      errorString += ` | Current value: ${valueString}`;
    }

    return errorString;
  }).join('\n');
}

/**
 * Hook-like function for form validation with enhanced debugging
 */
export function useValidation<T>(
  schema: { parse: (data: unknown) => T; pick?: (fields: Record<string, true>) => { parse: (data: unknown) => unknown } },
  schemaName?: string
): {
  validate: (data: unknown) => ValidationResult<T>;
  validateField: (field: string, value: unknown) => ValidationResult<unknown>;
} {
  return {
    validate: (data: unknown) => validateData(schema, data, schemaName),
    validateField: (field: string, value: unknown) => {
      // Create a partial schema for single field validation
      try {
        const partialSchema = schema.pick?.({ [field]: true }) || schema;
        return validateData(partialSchema, { [field]: value }, `${schemaName || 'Unknown'}.${field}`);
      } catch (error) {
        const currentUrl = typeof window !== 'undefined' ? window.location.href : 'unknown';
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : 'unknown';

        console.error(`💥 Error creating partial schema for field '${field}' at ${currentUrl}:`, error);
        return {
          success: false,
          errors: [{ field, message: 'Invalid field or schema error' }],
          debug: {
            totalErrors: 1,
            originalDataKeys: [field],
            timestamp: new Date().toISOString(),
            schemaName: `${schemaName || 'Unknown'}.${field}`,
            currentUrl,
            currentPath,
            userAgent: typeof window !== 'undefined' ? navigator.userAgent : 'unknown'
          }
        };
      }
    }
  };
}

// Additional validation functions with enhanced debugging
export const validateUpdateTenant = (data: unknown) =>
  validateData(UpdateTenantSchema, data, 'UpdateTenant');

export const validateCreateTenantUser = (data: unknown) =>
  validateData(CreateTenantUserSchema, data, 'CreateTenantUser');
